import {SingletonService} from '@/common/dependency.configs'
import {inject, injectable} from 'inversify'
import HttpHandler from '@/common/services/connect/HttpHandler'
import SERVICE_PATH_CONSTANTS from '@/common/constant/servicePathConstants'
import ResponseTypes from '@/common/enums/responseTypesEnum'
import type IApplicantInvite from '@/common/models/IApplicantInvite'
import INVITED_APPLICANT_OVERALL_STATUS from '@/common/enums/invitedApplicantOverallStatusEnum'
@SingletonService
@injectable()
class ApplicantInviteFactory {
  constructor(
    @inject(HttpHandler)
    private httpHandler: HttpHandler
  ) {}
  invite(
    workspaceUUID: string,
    applicantId: number,
    message: {
      inviteId: any
    }
  ) {
    return message.inviteId
      ? this.httpHandler.put(
          `${SERVICE_PATH_CONSTANTS.WORKSPACE_URL_TEMPLATE}/${workspaceUUID}/applicant/${applicantId}/invite/${message.inviteId}`,
          message,
          {},
          ResponseTypes.Payload
        )
      : this.httpHandler.post(
          `${SERVICE_PATH_CONSTANTS.WORKSPACE_URL_TEMPLATE}/${workspaceUUID}/applicant/${applicantId}/invite`,
          message,
          {},
          ResponseTypes.Payload
        )
  }

  getAll(workspaceUUID: string, applicantId: number): Promise<IApplicantInvite[]> {
    return this.httpHandler.get(
      `${SERVICE_PATH_CONSTANTS.WORKSPACE_URL_TEMPLATE}/${workspaceUUID}/applicant/${applicantId}/invite`,
      {},
      ResponseTypes.Payload
    )
  }

  get(workspaceUUID: string, applicantId: number, applicantInviteId: number) {
    return this.httpHandler.get(
      `${SERVICE_PATH_CONSTANTS.WORKSPACE_URL_TEMPLATE}/${workspaceUUID}/applicant/${applicantId}/invite/${applicantInviteId}`,
      {},
      ResponseTypes.Payload
    )
  }

  getOneInvite(workspaceUUID: string, applicantInviteId: number): Promise<IApplicantInvite> {
    return this.httpHandler.get(
      `${SERVICE_PATH_CONSTANTS.WORKSPACE_URL_TEMPLATE}/${workspaceUUID}/invite/${applicantInviteId}`,
      {},
      ResponseTypes.Payload
    )
  }

  send(workspaceUUID: string, applicantInviteId: number) {
    return this.httpHandler.post(
      `${SERVICE_PATH_CONSTANTS.WORKSPACE_URL_TEMPLATE}/${workspaceUUID}/invite/${applicantInviteId}/send`,
      {},
      {},
      ResponseTypes.Payload
    )
  }

  sendDisclosureEmails(workspaceUUID: string, applicantId: number, enrollmentId: number) {
    return this.httpHandler.put(
      `${SERVICE_PATH_CONSTANTS.WORKSPACE_URL_TEMPLATE}/${workspaceUUID}/enrollment/${enrollmentId}/applicant/${applicantId}/send`,
      {},
      {},
      ResponseTypes.Payload
    )
  }

  setInvitedApplicantAsCompleted(workspaceUUID: string, invitedApplicantId: number) {
    return this.httpHandler.put(
      `${SERVICE_PATH_CONSTANTS.WORKSPACE_URL_TEMPLATE}/${workspaceUUID}/invite/${invitedApplicantId}`,
      {},
      {},
      ResponseTypes.Payload
    )
  }

  validate(workspaceUUID: string, token: any) {
    return this.httpHandler.post(
      `${SERVICE_PATH_CONSTANTS.WORKSPACE_URL_TEMPLATE}/${workspaceUUID}/invite`,
      {inviteToken: token},
      {},
      ResponseTypes.Payload
    )
  }

  /**
   * Checks if the originator applicant can continue past the Invite Applicants state to the enrollment portion of the
   * workflow.  This will be allowed only after all invited applicants have successfully completed CIP (or have been
   * removed from the application).  Furthermore, there must be at least one invited applicant in order to proceed.
   */
  canOriginatorApplicantContinueFromInviteApplicantsState = (applicantInvites: IApplicantInvite[]) =>
    !!applicantInvites.length &&
    applicantInvites.every(
      (invitedApplicant) => invitedApplicant.overallStatus === INVITED_APPLICANT_OVERALL_STATUS.VERIFICATION_COMPLETE
    )

  /**
   * Checks if the originator applicant can continue past the Wall state to be able to save to the core.  This will be
   * allowed only after all invited applicants have completed their disclosures.
   */
  canOriginatorApplicantContinueFromWallState(invitedApplicants: IApplicantInvite[]): boolean {
    return invitedApplicants.length
      ? invitedApplicants.every(
          (invitedApplicant: IApplicantInvite) =>
            invitedApplicant.overallStatus === INVITED_APPLICANT_OVERALL_STATUS.DISCLOSURES_COMPLETE
        )
      : true
  }

  /**
   * Checks if an invited applicant can continue past the Wall state to be presented their final disclosures.  This
   * will be allowed only after the originator applicant has completed the enrollment portion of their own workflow and
   * passed through the 'Send Disclosure E-mails' state.
   */
  canInvitedApplicantContinueFromWallState(invitedApplicant: IApplicantInvite): boolean {
    return invitedApplicant.overallStatus === INVITED_APPLICANT_OVERALL_STATUS.DISCLOSURES_CONTINUATION_EMAIL_SENT
  }
}
export default ApplicantInviteFactory
